import { Box, Flex, Icon, LinkBox, LinkOverlay } from "@chakra-ui/react";
import moment from "moment";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { type NotificationDto, useMarkNotificationsAsViewedMutation } from "../../../autogen/bff-api";
import { momentLocaleFor } from "../../formatting/i18nFormatters";
import { getNotificationDescription } from "../../../pages/notifications/get-notification-description";
import { getNotificationLink } from "../../../pages/notifications/getNotificationLink";
import { CHIP_BG, CHIP_FG, notificationVisual } from "./notificationVisuals";

interface Props {
  notification: NotificationDto;
  selectedOrgId: string;
  onNavigate?: () => void;
}

export const NotificationRow = ({ notification, selectedOrgId, onNavigate }: Props) => {
  const { i18n } = useTranslation();
  const momentLocale = momentLocaleFor(i18n.language);
  const [markAsViewed] = useMarkNotificationsAsViewedMutation();
  const visual = notificationVisual(notification.type);
  const link = getNotificationLink(notification, selectedOrgId);
  const unread = !notification.viewed;

  return (
    <LinkBox
      position="relative"
      px="4"
      py="3"
      borderLeft="3px solid"
      borderLeftColor={unread ? "smPrimary" : "transparent"}
      bg="transparent"
      _hover={{ bg: "smBackgroundTertiary" }}
      onClick={() => markAsViewed({ markNotificationsRequest: { notificationIds: [notification.id] } })}
    >
      <Flex gap="3" align="flex-start" pr="5">
        <Flex w="34px" h="34px" rounded="lg" flexShrink={0} align="center" justify="center" bg={CHIP_BG[visual.colorScheme]}>
          <Icon as={visual.icon} color={CHIP_FG[visual.colorScheme]} boxSize="4" />
        </Flex>
        <Box flex="1" minW={0}>
          <Box fontSize="sm" color="smText">
            {link ? (
              <LinkOverlay as={RouterLink} to={link} onClick={onNavigate}>
                {getNotificationDescription(notification, momentLocale)}
              </LinkOverlay>
            ) : (
              getNotificationDescription(notification, momentLocale)
            )}
          </Box>
          <Box fontSize="xs" mt="0.5" color="smMuted">
            {moment(notification.createdAt).fromNow()}
          </Box>
        </Box>
      </Flex>
      {unread && (
        <Box data-testid="notification-unread-dot" position="absolute" right="3" top="50%" transform="translateY(-50%)" w="2" h="2" rounded="full" bg="smPrimary" />
      )}
    </LinkBox>
  );
};
